/**
 * BadgeBankView — Gestion de la banque de badges (admin)
 * Challenges V2 (Lot 2) — Modale unifiée (plus de modale dans modale)
 */

import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Award, AlertCircle, Upload, Link, Palette, Check, X } from "lucide-react";
import { badgesApi } from "../lib/api";
import { pickBadgeName, pickBadgeDescription } from "../lib/badge-i18n";
import { useResizable } from "../hooks/useResizable";
import {
  BADGE_SHAPES, BADGE_COLORS, BADGE_ICONS, ICON_COLORS,
  TEXT_SIZES, TEXT_POSITIONS, TEXT_FONTS,
  composeBadgeDataUrl,
  type BadgeComposition,
} from "../lib/badge-composer";

interface Badge {
  id: string;
  name: string;
  description: string | null;
  nameFr?: string | null;
  nameEn?: string | null;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  imageUrl: string;
  createdAt: string;
}

export function BadgeBankView() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === "fr";

  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState<Badge | null>(null);
  const [form, setForm] = useState({ name: "", description: "", imageUrl: "" });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [imageMode, setImageMode] = useState<"compose" | "url" | "upload">("compose");

  // Composer state (inline)
  const [shapeId, setShapeId] = useState("circle");
  const [bgColor, setBgColor] = useState("#F6C847");
  const [iconId, setIconId] = useState("trophy");
  const [iconColor, setIconColor] = useState("#FFFFFF");
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [textSize, setTextSize] = useState(20);
  const [textPosition, setTextPosition] = useState("bottom");
  const [textFont, setTextFont] = useState("Sora, sans-serif");

  // File upload ref
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Resize
  const { containerRef, onPointerDown, boxStyle } = useResizable({
    minWidth: 400,
    minHeight: 500,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 40 : 1000,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 40 : 900,
    defaultWidth: 560,
    defaultHeight: 720,
  });

  // Delete confirm
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");

  // Composer preview
  const composition: BadgeComposition = useMemo(() => ({
    shapeId, bgColor, iconId, iconColor,
    text: text.trim() || undefined,
    textColor: text.trim() ? textColor : undefined,
    textSize: text.trim() ? textSize : undefined,
    textPosition: text.trim() ? textPosition : undefined,
    textFont: text.trim() ? textFont : undefined,
  }), [shapeId, bgColor, iconId, iconColor, text, textColor, textSize, textPosition, textFont]);

  const composerPreviewUrl = useMemo(() => composeBadgeDataUrl(composition), [composition]);

  // Auto-sync composer preview → form.imageUrl when in compose mode
  useEffect(() => {
    if (imageMode === "compose") {
      setForm((f) => ({ ...f, imageUrl: composerPreviewUrl }));
    }
  }, [composerPreviewUrl, imageMode]);

  const loadBadges = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await badgesApi.getAll();
      setBadges(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBadges(); }, []);

  const openCreate = () => {
    setEditingBadge(null);
    setForm({ name: "", description: "", imageUrl: "" });
    setImageMode("compose");
    setShapeId("circle"); setBgColor("#F6C847"); setIconId("trophy"); setIconColor("#FFFFFF");
    setText(""); setTextColor("#FFFFFF"); setTextSize(20); setTextPosition("bottom"); setTextFont("Sora, sans-serif");
    setModalError("");
    setModalOpen(true);
  };

  const openEdit = (badge: Badge) => {
    setEditingBadge(badge);
    // L'édition est traitée comme du FR : on pré-remplit avec la variante FR
    // (fallback legacy `name`/`description` pour les badges pas encore migrés).
    setForm({
      name: badge.nameFr || badge.name,
      description: badge.descriptionFr || badge.description || "",
      imageUrl: badge.imageUrl,
    });
    setImageMode(badge.imageUrl.startsWith("data:image/svg+xml") ? "compose" : badge.imageUrl.startsWith("data:") ? "upload" : "url");
    setModalError("");
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setModalError(isFr ? "Le nom du badge est requis." : "Badge name is required."); return; }
    if (!form.imageUrl.trim()) { setModalError(isFr ? "L'image du badge est requise. Composez un badge ou importez une image." : "Badge image is required. Compose a badge or import an image."); return; }

    setSaving(true);
    setModalError("");
    try {
      if (editingBadge) {
        const updated = await badgesApi.update(editingBadge.id, form);
        setBadges((prev) => prev.map((b) => b.id === updated.id ? updated : b));
      } else {
        const created = await badgesApi.create(form);
        setBadges((prev) => [...prev, created]);
      }
      setModalOpen(false);
    } catch (err: any) {
      setModalError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteError("");
    try {
      await badgesApi.remove(id);
      setBadges((prev) => prev.filter((b) => b.id !== id));
      setDeletingId(null);
    } catch (err: any) {
      setDeleteError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5" style={{ color: "#F6C847" }} />
          <h2 className="text-lg font-bold" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
            {isFr ? "Banque de badges" : "Badge bank"}
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700">{badges.length}</span>
        </div>
        <Button onClick={openCreate} size="sm" style={{ backgroundColor: "#F6C847", color: "#926B00" }} className="hover:opacity-90">
          <Plus className="h-4 w-4 mr-1" />
          {isFr ? "Nouveau badge" : "New badge"}
        </Button>
      </div>

      {/* Loading / Error */}
      {loading && <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
      {error && <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3 text-sm"><AlertCircle className="h-4 w-4 shrink-0" /><p>{error}</p></div>}

      {/* Badge grid */}
      {!loading && badges.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Award className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>{isFr ? "Aucun badge créé pour le moment." : "No badges created yet."}</p>
        </div>
      )}
      {!loading && badges.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {badges.map((badge) => (
            <Card key={badge.id} className="group relative overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-lg overflow-hidden border border-gray-100">
                  <img src={badge.imageUrl} alt={pickBadgeName(badge, i18n.language)} className="w-full h-full object-contain" loading="lazy" />
                </div>
                <p className="font-semibold text-sm truncate" style={{ color: "#384872" }}>{pickBadgeName(badge, i18n.language)}</p>
                {pickBadgeDescription(badge, i18n.language) && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{pickBadgeDescription(badge, i18n.language)}</p>}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(badge)} className="p-1 rounded bg-white shadow-sm border hover:bg-gray-50" aria-label={isFr ? "Modifier" : "Edit"}>
                    <Pencil className="h-3 w-3 text-gray-600" />
                  </button>
                  <button onClick={() => { setDeletingId(badge.id); setDeleteError(""); }} className="p-1 rounded bg-white shadow-sm border hover:bg-red-50" aria-label={isFr ? "Supprimer" : "Delete"}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== MODALE UNIQUE : Créer/Éditer badge ===== */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" onClick={() => { if (!document.body.style.cursor) setModalOpen(false); }}>
          <div
            ref={containerRef}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
            style={boxStyle(560, 720)}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b shrink-0" style={{ backgroundColor: "#F6C847" + "20" }}>
              <h2 className="font-bold text-lg" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
                {editingBadge ? (isFr ? "Modifier le badge" : "Edit badge") : (isFr ? "Créer un badge" : "Create badge")}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
              {/* Preview + Nom/Description */}
              <div className="flex gap-4 items-start">
                {/* Preview */}
                <div className="shrink-0">
                  {form.imageUrl ? (
                    <div className="w-20 h-20 rounded-lg border-2 border-[#F6C847]/30 overflow-hidden bg-gray-50">
                      <img src={form.imageUrl} alt="Preview" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <Award className="h-8 w-8 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <Label htmlFor="badge-name" className="text-xs">{isFr ? "Nom du badge *" : "Badge name *"}</Label>
                    <Input id="badge-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={isFr ? "Ex : Champion Prospection" : "E.g. Prospecting Champion"} disabled={saving} />
                  </div>
                  <div>
                    <Label htmlFor="badge-desc" className="text-xs">{isFr ? "Description" : "Description"}</Label>
                    <Input id="badge-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder={isFr ? "Décrivez le badge..." : "Describe the badge..."} disabled={saving} />
                  </div>
                </div>
              </div>

              {/* Tabs image mode */}
              <div>
                <Label className="text-xs">{isFr ? "Image du badge *" : "Badge image *"}</Label>
                <div className="flex gap-1 mt-1 mb-3">
                  {([
                    { mode: "compose" as const, icon: Palette, label: isFr ? "Composer" : "Compose" },
                    { mode: "upload" as const, icon: Upload, label: isFr ? "Fichier" : "File" },
                    { mode: "url" as const, icon: Link, label: "URL" },
                  ]).map(({ mode, icon: Icon, label }) => (
                    <button key={mode} onClick={() => setImageMode(mode)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border transition-all ${
                        imageMode === mode ? "border-[#384872] bg-[#384872] text-white font-semibold" : "border-gray-200 hover:border-gray-300"
                      }`}>
                      <Icon className="h-3 w-3" />{label}
                    </button>
                  ))}
                </div>

                {/* === MODE COMPOSER (inline) === */}
                {imageMode === "compose" && (
                  <div className="space-y-3 p-3 rounded-lg border border-[#F6C847]/30 bg-yellow-50/30">
                    {/* Forme */}
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{isFr ? "Forme" : "Shape"}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {BADGE_SHAPES.map((s) => (
                          <button key={s.id} onClick={() => setShapeId(s.id)}
                            className={`px-2 py-1 text-[10px] rounded border ${shapeId === s.id ? "border-[#F6C847] bg-yellow-50 font-semibold" : "border-gray-200"}`}>
                            {isFr ? s.label.fr : s.label.en}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Couleur fond */}
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{isFr ? "Couleur de fond" : "Background"}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {BADGE_COLORS.map((c) => (
                          <button key={c.id} onClick={() => setBgColor(c.hex)}
                            className={`w-6 h-6 rounded border ${bgColor === c.hex ? "border-[#384872] ring-1 ring-[#384872] scale-110" : "border-gray-300"}`}
                            style={{ backgroundColor: c.hex }} title={isFr ? c.label.fr : c.label.en}>
                            {bgColor === c.hex && <Check className={`h-3 w-3 mx-auto ${c.hex === "#FFFFFF" ? "text-gray-800" : "text-white"} drop-shadow`} />}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Icône */}
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{isFr ? "Icône" : "Icon"}</span>
                      <div className="max-h-[130px] overflow-y-auto rounded border border-gray-100 p-1 mt-1">
                        <div className="grid grid-cols-7 gap-0.5">
                          {BADGE_ICONS.map((icon) => {
                            const miniSvg = composeBadgeDataUrl({ shapeId: "circle", bgColor, iconId: icon.id, iconColor });
                            return (
                              <button key={icon.id} onClick={() => setIconId(icon.id)}
                                className={`p-0.5 rounded border ${iconId === icon.id ? "border-[#F6C847] bg-yellow-50" : "border-transparent hover:border-gray-200"}`}
                                title={isFr ? icon.label.fr : icon.label.en}>
                                <img src={miniSvg} alt={icon.label.fr} className="w-full h-auto" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    {/* Couleur icône */}
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{isFr ? "Couleur icône" : "Icon color"}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ICON_COLORS.map((c) => (
                          <button key={c.id} onClick={() => setIconColor(c.hex)}
                            className={`w-5 h-5 rounded border ${iconColor === c.hex ? "border-[#384872] ring-1 ring-[#384872]" : "border-gray-300"}`}
                            style={{ backgroundColor: c.hex }} title={isFr ? c.label.fr : c.label.en} />
                        ))}
                      </div>
                    </div>
                    {/* Texte optionnel */}
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{isFr ? "Texte (optionnel)" : "Text (optional)"}</span>
                      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={isFr ? "#1, PRO, TOP..." : "#1, PRO, TOP..."} maxLength={15} className="mt-1 h-8 text-sm" />
                      {text.trim() && (
                        <div className="space-y-1.5 mt-2 pl-2 border-l-2 border-[#F6C847]/30">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-12 shrink-0">{isFr ? "Police" : "Font"}</span>
                            <div className="flex gap-1">
                              {TEXT_FONTS.map((f) => (
                                <button key={f.id} onClick={() => setTextFont(f.value)}
                                  className={`px-2 py-0.5 text-[10px] rounded border ${textFont === f.value ? "border-[#384872] bg-blue-50 font-semibold" : "border-gray-200"}`}
                                  style={{ fontFamily: f.value }}>{f.id === "sora" ? "Sora" : "Inter"}</button>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-12 shrink-0">{isFr ? "Taille" : "Size"}</span>
                            <div className="flex gap-1">
                              {TEXT_SIZES.map((s) => (
                                <button key={s.id} onClick={() => setTextSize(s.value)}
                                  className={`px-2 py-0.5 text-[10px] rounded border ${textSize === s.value ? "border-[#384872] bg-blue-50 font-semibold" : "border-gray-200"}`}>
                                  {isFr ? s.label.fr : s.label.en}</button>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-12 shrink-0">Position</span>
                            <div className="flex gap-1">
                              {TEXT_POSITIONS.map((p) => (
                                <button key={p.id} onClick={() => setTextPosition(p.id)}
                                  className={`px-2 py-0.5 text-[10px] rounded border ${textPosition === p.id ? "border-[#384872] bg-blue-50 font-semibold" : "border-gray-200"}`}>
                                  {isFr ? p.label.fr : p.label.en}</button>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-12 shrink-0">{isFr ? "Couleur" : "Color"}</span>
                            <div className="flex flex-wrap gap-1">
                              {ICON_COLORS.slice(0, 12).map((c) => (
                                <button key={c.id} onClick={() => setTextColor(c.hex)}
                                  className={`w-4 h-4 rounded border ${textColor === c.hex ? "border-[#384872] ring-1 ring-[#384872]" : "border-gray-300"}`}
                                  style={{ backgroundColor: c.hex }} title={isFr ? c.label.fr : c.label.en} />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* === MODE URL === */}
                {imageMode === "url" && (
                  <div className="space-y-2 p-3 rounded-lg border border-gray-200">
                    <Input value={form.imageUrl.startsWith("data:") ? "" : form.imageUrl}
                      onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                      placeholder="https://www.svgrepo.com/..." disabled={saving} />
                    <a href="https://www.svgrepo.com/vectors/badge/" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#5A4BBE] hover:underline">
                      <Link className="h-3 w-3" />
                      {isFr ? "Parcourir SVG Repo" : "Browse SVG Repo"}
                    </a>
                  </div>
                )}

                {/* === MODE UPLOAD === */}
                {imageMode === "upload" && (
                  <div className="p-3 rounded-lg border border-gray-200">
                    <input ref={fileInputRef} type="file" accept=".svg,.png,.jpg,.jpeg,.webp" className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 200 * 1024) { setModalError(isFr ? "Fichier trop volumineux (max 200 KB)." : "File too large (max 200 KB)."); return; }
                        const reader = new FileReader();
                        reader.onload = () => { setForm({ ...form, imageUrl: reader.result as string }); setModalError(""); };
                        reader.readAsDataURL(file);
                      }} />
                    <Button type="button" variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()} disabled={saving}>
                      <Upload className="h-4 w-4 mr-2" />
                      {isFr ? "Choisir un fichier (SVG, PNG, max 200 KB)" : "Choose file (SVG, PNG, max 200 KB)"}
                    </Button>
                  </div>
                )}
              </div>

              {/* Error */}
              {modalError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4 shrink-0" /><p>{modalError}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-3 border-t bg-gray-50/50 shrink-0">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
                {isFr ? "Annuler" : "Cancel"}
              </Button>
              <Button onClick={handleSave} disabled={saving || !form.name.trim() || !form.imageUrl.trim()}
                style={{ backgroundColor: "#F6C847", color: "#926B00" }} className="hover:opacity-90">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingBadge ? (isFr ? "Enregistrer" : "Save") : (isFr ? "Créer le badge" : "Create badge")}
              </Button>
            </div>

            {/* Resize handles */}
            <div className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize hidden sm:block" onPointerDown={onPointerDown("right")} />
            <div className="absolute bottom-0 left-0 w-full h-1.5 cursor-ns-resize hidden sm:block" onPointerDown={onPointerDown("bottom")} />
            <div className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize hidden sm:flex items-end justify-end p-0.5" onPointerDown={onPointerDown("corner")}>
              <svg width="10" height="10" viewBox="0 0 10 10" className="text-gray-400">
                <circle cx="8" cy="2" r="1" fill="currentColor" /><circle cx="8" cy="5.5" r="1" fill="currentColor" />
                <circle cx="8" cy="9" r="1" fill="currentColor" /><circle cx="4.5" cy="5.5" r="1" fill="currentColor" />
                <circle cx="4.5" cy="9" r="1" fill="currentColor" /><circle cx="1" cy="9" r="1" fill="currentColor" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete dialog */}
      <Dialog open={!!deletingId} onOpenChange={(v) => { if (!v) { setDeletingId(null); setDeleteError(""); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{isFr ? "Supprimer le badge" : "Delete badge"}</DialogTitle>
            <DialogDescription>{isFr ? "Cette action est irréversible." : "This action is irreversible."}</DialogDescription>
          </DialogHeader>
          {deleteError && <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3"><AlertCircle className="h-4 w-4 shrink-0" /><p>{deleteError}</p></div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeletingId(null); setDeleteError(""); }}>{isFr ? "Annuler" : "Cancel"}</Button>
            <Button variant="destructive" onClick={() => deletingId && handleDelete(deletingId)}>
              <Trash2 className="h-4 w-4 mr-2" />{isFr ? "Supprimer" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
